import React, { useMemo, useState } from 'react'
import classnames from 'classnames/bind'
import { Text, CardContainer, InfoPill } from '../elementals'
import ExpandableCard from '../layouts/expandableCard'
import { formatters } from '../../utils'
import { Maybe, ListingParty, CurrentTargetAudience } from '@adc/sdk/types'
import styles from './styles.scss'
import * as graph from '@adc/sdk'
import { ColorNames } from '../../constants'
import { TextTruncate } from '../layouts'
import { findSellerRepresentative } from './utils'

const HEADER_HEIGHT = 24 + 18 * 2

const cx = classnames.bind(styles)

type ListingSellerRepresentativeCardProps = {
  listingParties: Array<Maybe<ListingParty>>
  title?: string
  className?: string
  open?: boolean
  initialActiveState?: boolean
  viewingFrom?: CurrentTargetAudience
}

const ListingSellerRepresentativeCard = ({
  listingParties,
  title = 'Listing Agent',
  className,
  open = false,
  initialActiveState = false,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
}: ListingSellerRepresentativeCardProps) => {
  const isBuyerView = viewingFrom === graph.enums.CurrentTargetAudience.Buyer

  const INFO_HEIGHT = useMemo(() => (isBuyerView ? 120 : 138), [isBuyerView])

  const sellerRepresentativeParty = findSellerRepresentative(
    listingParties,
    viewingFrom
  )
  if (!sellerRepresentativeParty) return null

  const {
    party_name: partyName,
    party_phone: partyPhone,
    party_email: partyEmail,
  } = sellerRepresentativeParty
  const [active, setActive] = useState(initialActiveState || open)

  const agentInformation = () => (
    <div
      data-elm-id={`listing-agent-card-${partyName || ''}`}
      className={cx('card')}
    >
      <div className={cx('agent-detail')}>
        <div className={cx('detail-text')}>
          {!isBuyerView ? (
            <Text size='tiny' color='grayMedium' className={cx('label-text')}>
              Representative Agent
            </Text>
          ) : undefined}

          <TextTruncate
            color='gray'
            data-elm-id='representative-agent'
            ellipsis='...'
            readMoreLabel={null}
            size='tiny'
            weight='heavy'
            width={310}
          >
            {partyName}
          </TextTruncate>
        </div>
      </div>
      <div className={cx('agent-detail')}>
        <InfoPill
          size='small'
          includeBorder
          backgroundColor={ColorNames.InfoLight}
          foregroundColor={ColorNames.Info}
          iconName='phone'
          extraPadding='small'
          iconStyle='regular'
        />
        <div className={cx('detail-text')}>
          <Text
            weight='heavy'
            size='tiny'
            data-elm-id='representative-agent-phone'
          >
            <a href={`tel:${partyPhone || ''}`}>
              {partyPhone && formatters.formatPhoneNumber(partyPhone)}
            </a>
          </Text>
        </div>
      </div>
      <div className={cx('agent-detail')}>
        <InfoPill
          size='small'
          includeBorder
          backgroundColor={ColorNames.InfoLight}
          foregroundColor={ColorNames.Info}
          iconName='envelope'
          extraPadding='small'
          iconStyle='regular'
        />
        <div className={cx('detail-text')}>
          <a href={`mailto:${partyEmail || ''}`}>
            <TextTruncate
              color={ColorNames.Info}
              data-elm-id='representative-agent-email'
              ellipsis='...'
              readMoreLabel={null}
              size='tiny'
              weight='heavy'
              width={280}
            >
              {partyEmail}
            </TextTruncate>
          </a>
        </div>
      </div>
    </div>
  )

  const slots = {
    card: ({
      elmId,
      children,
      className,
    }: {
      elmId?: string
      children?: React.ReactNode
      className?: string
    } = {}) => (
      <CardContainer
        elmId={elmId as string}
        className={className as string}
        borderRadius='small'
        padding='small'
      >
        {children}
      </CardContainer>
    ),
    header: () => (
      <div style={{ width: '100%' }}>
        <Text.Body size='large' weight='heavy'>
          {title}
        </Text.Body>
      </div>
    ),
    content: agentInformation,
  }

  return (
    <ExpandableCard
      slots={slots}
      active={active}
      onClick={open ? undefined : () => setActive(!active)}
      classNames={{ card: cx('card', className) }}
      heightHints={{
        collapsed: HEADER_HEIGHT,
        expanded: INFO_HEIGHT + HEADER_HEIGHT,
      }}
    />
  )
}

export default ListingSellerRepresentativeCard
